import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Save, Check, Loader2, FileText } from "lucide-react";
import { AppHeader } from "../../components/AppHeader";
import { FieldRenderer } from "../../components/FieldRenderer";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { SECTIONS, FILE_KEYS } from "../../lib/assessmentConfig";
import api from "../../lib/api";

const REVIEW = SECTIONS.length; // index for the review step

function visible(block, answers) {
  if (!block.showIf) return true;
  return answers[block.showIf.key] === block.showIf.eq;
}

function effectiveRequired(block, answers) {
  if (block.required) return true;
  if (block.requiredIf) {
    const conds = block.requiredIf.anyOf || [block.requiredIf];
    return conds.some((c) => answers[c.key] === c.eq);
  }
  return false;
}

function isFilled(block, answers, docs) {
  if (block.type === "info") return true;
  if (block.input === "file") {
    if (!effectiveRequired(block, answers)) return true;
    return (docs || []).some((d) => d.field === block.key);
  }
  const v = answers[block.key];
  if (block.type === "ack") return !!v;
  if (block.input === "checkboxes") return Array.isArray(v) && v.length > 0;
  return typeof v === "string" ? v.trim().length > 0 : v != null && v !== "";
}

function sectionMissing(section, answers, docs) {
  return section.blocks
    .filter((b) => (b.type === "field" || b.type === "ack") && visible(b, answers) && !isFilled(b, answers, docs))
    .map((b) => b.key);
}

function sectionHasFields(section) {
  return section.blocks.some((b) => b.type === "field" || b.type === "ack");
}

export default function AssessmentWizard() {
  const [answers, setAnswers] = useState(null);
  const [docs, setDocs] = useState([]);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [signature, setSignature] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/application/me");
        if (!["ALIGNED", "UNSURE"].includes(data.culture_alignment)) { navigate("/culture", { replace: true }); return; }
        if (data.locked) { navigate("/portal", { replace: true }); return; }
        setAnswers(data.answers || {});
        setDocs(data.documents || []);
        setSignature(data.answers?.legal_name || "");
      } catch { toast.error("Could not load your assessment."); }
    })();
  }, [navigate]);

  const setField = (key, val) => {
    setAnswers((a) => ({ ...a, [key]: val }));
    setErrors((e) => e.filter((k) => k !== key));
  };
  const onUploaded = (doc) => setDocs((d) => [...d.filter((x) => x.field !== doc.field), doc]);

  const save = async (silent = false) => {
    setSaving(true);
    try {
      await api.put("/application/me/save", { answers });
      if (!silent) toast.success("Progress saved");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      return false;
    } finally { setSaving(false); }
  };

  const goNext = async () => {
    if (section) {
      const missing = sectionMissing(section, answers, docs);
      if (missing.length) {
        setErrors(missing);
        toast.error("Please complete every field before continuing. Enter “NA” if a question does not apply.");
        window.scrollTo(0, 0);
        return;
      }
    }
    setErrors([]);
    const ok = await save(true);
    if (ok) { setStep((s) => Math.min(s + 1, REVIEW)); window.scrollTo(0, 0); }
  };
  const goPrev = () => { setErrors([]); setStep((s) => Math.max(s - 1, 0)); window.scrollTo(0, 0); };

  const requiredAcks = useMemo(() => {
    const keys = [];
    SECTIONS.forEach((s) => s.blocks.forEach((b) => { if (b.type === "ack") keys.push(b.key); }));
    return keys;
  }, []);

  const allAcksChecked = requiredAcks.every((k) => !!answers?.[k]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/application/me/submit", { answers, signature_name: signature });
      setShowConfirm(false);
      toast.success("Assessment submitted");
      navigate("/portal", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const attemptSubmit = async () => {
    for (let i = 0; i < SECTIONS.length; i++) {
      const m = sectionMissing(SECTIONS[i], answers, docs);
      if (m.length) {
        await save(true);
        setErrors(m);
        setStep(i);
        window.scrollTo(0, 0);
        toast.error(`Please complete “${SECTIONS[i].title}” before submitting.`);
        return;
      }
    }
    setShowConfirm(true);
  };

  if (!answers) return <div className="min-h-screen bg-[#FAF9F6]" />;

  const section = step < REVIEW ? SECTIONS[step] : null;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <AppHeader />

      {/* Top stepper */}
      <div className="border-b border-[#E5E2DC] bg-white">
        <div className="mx-auto max-w-5xl px-4 py-4 sm:px-6">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-medium text-[#1F2924]">
              {step < REVIEW ? section.title : "Review My Answers"}
            </span>
            <span className="text-[#515E58]">
              {step < REVIEW ? `Step ${step + 1} of ${SECTIONS.length}` : "Final Review"}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-[#E5E2DC]">
            <div className="h-full rounded-full bg-[#2C5545] transition-all duration-300"
              style={{ width: `${((step + 1) / (SECTIONS.length + 1)) * 100}%` }} />
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            {step < REVIEW ? (
              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-[#515E58]">{section.subtitle}</p>
                <h2 className="mt-1 font-heading text-2xl font-semibold text-[#1F2924] sm:text-3xl">{section.title}</h2>
                {sectionHasFields(section) && !section.readonly && (
                  <div className="mt-3 flex items-start gap-2 rounded-lg border border-[#E89B31]/30 bg-[#FDF6EA] px-4 py-3 text-sm text-[#B4791F]" data-testid="required-notice">
                    <span>All questions on this page are required. If a question does not apply to you, enter “NA” or select the closest option.</span>
                  </div>
                )}
                <div className="mt-6 space-y-6">
                  {section.blocks.map((block, i) =>
                    visible(block, answers) ? (
                      <FieldRenderer key={block.key || `b-${i}`}
                        block={block.requiredIf ? { ...block, required: effectiveRequired(block, answers) } : block}
                        value={answers[block.key]} onChange={(v) => setField(block.key, v)}
                        docs={docs} onUploaded={onUploaded} readonly={section.readonly && block.type !== "info"}
                        error={errors.includes(block.key)} />
                    ) : null
                  )}
                </div>
              </div>
            ) : (
              <ReviewStep answers={answers} docs={docs} onEdit={(i) => { setStep(i); window.scrollTo(0, 0); }}
                signature={signature} setSignature={setSignature}
                confirmChecked={confirmChecked} setConfirmChecked={setConfirmChecked}
                allAcksChecked={allAcksChecked} onSubmit={attemptSubmit} />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Nav buttons */}
        <div className="mt-10 flex items-center justify-between border-t border-[#E5E2DC] pt-6">
          <Button variant="outline" onClick={goPrev} disabled={step === 0} data-testid="wizard-prev"
            className="gap-2 border-[#E5E2DC] text-[#515E58] hover:bg-[#F4F2EC]">
            <ArrowLeft className="h-4 w-4" /> Previous
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="wizard-save"
              className="gap-2 border-[#2C5545] text-[#2C5545] hover:bg-[#EAF0ED]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </Button>
            {step < REVIEW && (
              <Button onClick={goNext} data-testid="wizard-next"
                className="gap-2 rounded-lg bg-[#2C5545] px-6 hover:bg-[#224336] transition-transform hover:-translate-y-[1px]">
                Save &amp; Continue <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </main>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent data-testid="submit-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Ready to submit?</DialogTitle>
            <DialogDescription>
              I am ready to submit my completed assessment. After submission, your answers will be locked from
              editing unless an administrator reopens the assessment.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfirm(false)} className="border-[#E5E2DC]">Cancel</Button>
            <Button onClick={submit} disabled={submitting} data-testid="submit-confirm-btn"
              className="gap-2 bg-[#2C5545] hover:bg-[#224336]">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Submit My Assessment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function renderValue(block, answers, docs) {
  const v = answers[block.key];
  if (block.input === "file") {
    const d = docs.find((x) => x.field === block.key);
    return d ? d.original_filename : "—";
  }
  if (block.type === "ack") return v ? "Acknowledged" : "Not acknowledged";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  return v || "—";
}

function ReviewStep({ answers, docs, onEdit, signature, setSignature, confirmChecked, setConfirmChecked, allAcksChecked, onSubmit }) {
  const CERT_KEYS = ["cert_1", "cert_2", "cert_3", "cert_4"];
  const certLabels = SECTIONS.find((s) => s.id === "certification").blocks.filter((b) => b.type === "ack");
  const canSubmit = allAcksChecked && signature.trim().length > 1 && confirmChecked;

  return (
    <div data-testid="review-step">
      <p className="text-xs uppercase tracking-[0.14em] text-[#515E58]">Final Step</p>
      <h2 className="mt-1 font-heading text-2xl font-semibold text-[#1F2924] sm:text-3xl">Review My Answers</h2>
      <p className="mt-2 text-[#515E58]">Please review your responses below before submitting. You can edit any section.</p>

      <div className="mt-6 space-y-5">
        {SECTIONS.map((s, idx) => {
          const fields = s.blocks.filter((b) => b.type === "field" || b.type === "ack");
          if (!fields.length) return null;
          return (
            <div key={s.id} className="rounded-xl border border-[#E5E2DC] bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-heading text-lg font-semibold text-[#1F2924]">{s.title}</h3>
                <button onClick={() => onEdit(idx)} className="text-sm font-medium text-[#2C5545] hover:underline"
                  data-testid={`review-edit-${s.id}`}>Edit</button>
              </div>
              <dl className="space-y-3">
                {fields.map((b) => (
                  <div key={b.key} className="grid grid-cols-1 gap-1 sm:grid-cols-[1fr_1.2fr] sm:gap-4">
                    <dt className="text-sm text-[#515E58]">{b.label}</dt>
                    <dd className="text-sm text-[#1F2924] whitespace-pre-wrap">{renderValue(b, answers, docs)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          );
        })}
      </div>

      {/* Certification signature */}
      <div className="mt-6 rounded-xl border border-[#2C5545]/30 bg-[#EAF0ED] p-6">
        <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-[#1F2924]">
          <FileText className="h-5 w-5 text-[#2C5545]" /> Electronic Acknowledgment
        </h3>
        {!allAcksChecked && (
          <p className="mt-2 text-sm text-[#C44F4F]" data-testid="ack-warning">
            All acknowledgment and certification checkboxes must be completed before you can submit. Use "Edit" above to review Sections 5 and 9.
          </p>
        )}
        <div className="mt-4 space-y-2">
          {certLabels.map((c) => (
            <div key={c.key} className="flex items-start gap-2 text-sm text-[#1F2924]">
              <Check className={`mt-0.5 h-4 w-4 ${answers[c.key] ? "text-[#3A7D5E]" : "text-[#E5E2DC]"}`} />
              <span>{c.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-5">
          <label className="text-sm font-medium text-[#1F2924]">Type your full legal name as your electronic signature</label>
          <Input value={signature} onChange={(e) => setSignature(e.target.value)} className="mt-2 bg-white"
            placeholder="Full legal name" data-testid="signature-input" />
        </div>
        <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-[#E5E2DC] bg-white p-4">
          <Checkbox checked={confirmChecked} onCheckedChange={(v) => setConfirmChecked(!!v)} data-testid="final-confirm" className="mt-0.5" />
          <span className="text-sm text-[#1F2924]">I am ready to submit my completed assessment.</span>
        </label>
        <Button onClick={onSubmit} disabled={!canSubmit} data-testid="submit-assessment-btn"
          className="mt-5 w-full gap-2 rounded-lg bg-[#2C5545] py-6 text-base font-semibold hover:bg-[#224336] disabled:opacity-50">
          <Check className="h-5 w-5" /> Submit My Assessment
        </Button>
      </div>
    </div>
  );
}
